import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import Icon from './Icon';
import BrandLogo from './BrandLogo';
import { Button } from './DesignSystem';
import { getNavItemsForDesktopHeader, getNavItemsForDesktopDropdown } from '../utils/navigationUtils';
import { getUserInitials as getInitials, getUserDisplayName, getPremiumBadge } from '../utils/userDisplay';

type ExtendedUser = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  unread_notifications?: number;
  notifications_unread?: number;
  unread?: number;
};

const Header: React.FC<{ userRole?: string }> = memo(({ userRole: propUserRole }) => {
  const location = useLocation();
  const { token, user, logout } = useAuth();
  const { theme, getThemeStyles } = useTheme();
  const themeStyles = getThemeStyles();
  // notifications context (WS + polling handled by provider)
  const { notifications, unreadCount: notifUnreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const toggleSettings = useCallback(() => {
    setShowSettings(prev => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showSettings || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings, showNotifications]);

  const hideMenu = false; // Show header on all pages

  let userRole = propUserRole;
  if (!token && !['/login', '/register'].includes(location.pathname)) {
    userRole = 'guest';
  }
  if (!token && location.pathname === '/') {
    userRole = 'guest';
  }
  const isGuest = !token && userRole === 'guest';

  const setLastClickedRoute = (route: string) => {
    try {
      localStorage.setItem('lastClickedRoute', route);
    } catch {
      // Ignore localStorage errors in incognito mode
    }
  };

  // friendly unread notifications count (defensive: support multiple possible field names)
  // some user shapes may not declare a typed field for unread counts
  const unreadCount: number = ((user as ExtendedUser)?.unread_notifications ?? (user as ExtendedUser)?.notifications_unread ?? (user as ExtendedUser)?.unread ?? 0);

  const getNavItems = () => {
    // prefer actual authenticated user's role (user.role) to ensure items show
    const role = (user && (user.role as string)) || propUserRole || userRole;
    return getNavItemsForDesktopDropdown(role);
  };

  return (
    <header
      className="header-desktop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1001,
        padding: '0.85rem clamp(1.8rem, 4vw, 3.5rem)',
        minHeight: 'var(--header-height)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1.5rem',
        background: themeStyles.cardBackground,
        borderBottom: `1px solid ${themeStyles.border}`,
        boxShadow: themeStyles.cardShadow,
        backdropFilter: 'blur(6px) saturate(120%)',
        WebkitBackdropFilter: 'blur(6px) saturate(120%)'
      }}
    >

      {/* Logo Grented */}
      <Link
        to="/"
        style={{
          textDecoration: 'none',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <BrandLogo height={110} />
          {/* Trust badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: themeStyles.textSecondary,
            fontSize: 12,
            padding: '6px 10px',
            borderRadius: 12,
            border: `1px solid ${themeStyles.border}`,
            background: 'rgba(255,255,255,0.04)'
          }}>
            <span aria-hidden>🔒</span>
            <span>Connessione protetta</span>
            <span style={{ opacity: 0.5 }}>•</span>
            <span>Verifiche documentali</span>
          </div>
        </div>
      </Link>

      {/* Navigation items for header */}
      {token && user && (
        <nav style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {getNavItemsForDesktopHeader(user.role).map((item, index) => {
            const isActive = window.location.pathname === item.path;
            return (
              <button
                key={`header-${index}`}
                style={{
                  background: isActive ? themeStyles.primary : 'transparent',
                  border: isActive ? `1px solid ${themeStyles.primary}` : `1px solid ${themeStyles.border}`,
                  borderRadius: '10px',
                  padding: '10px 14px',
                  color: isActive ? '#ffffff' : themeStyles.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: 600,
                  boxShadow: isActive ? '0 4px 12px rgba(15,23,42,0.08)' : 'none'
                }}
                onClick={() => navigate(item.path)}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = themeStyles.hover;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon name={item.icon} size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {/* Avatar and controls (avatar + settings dropdown) */}
      {token && user && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Notification bell - left of avatar */}
          <div style={{ position: 'relative' }} ref={notificationsRef}>
            <button
              aria-label="Apri notifiche"
              onClick={() => setShowNotifications(v => !v)}
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f1f5f9',
                border: '1px solid rgba(2,6,23,0.06)',
                color: '#0f172a',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `${themeStyles.glow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(255, 255, 255, 0.05)';
              }}
            >
              {/* bell icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M15 17H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 3C10.3431 3 9 4.34315 9 6V7.089C6.165 8.042 4 10.828 4 14V17L3 18V19H21V18L20 17V14C20 10.828 17.835 8.042 15 7.089V6C15 4.34315 13.6569 3 12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {notifUnreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: -6,
                right: -6,
                minWidth: 18,
                height: 18,
                padding: '0 4px',
                borderRadius: 12,
                background: '#ef4444',
                color: 'white',
                fontSize: 11,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>{notifUnreadCount > 99 ? '99+' : notifUnreadCount}</span>
            )}
            {showNotifications && (
              <div style={{
                position: 'absolute',
                top: '110%',
                right: 0,
                width: 360,
                maxHeight: 480,
                overflow: 'auto',
                background: themeStyles.cardBackground,
                border: `1px solid ${themeStyles.border}`,
                borderRadius: 18,
                boxShadow: themeStyles.cardShadow,
                zIndex: 1300,
                padding: '0.5rem'
              }}>
                <div style={{ padding: '0.6rem 0.8rem', borderBottom: `1px solid ${themeStyles.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>Notifiche</strong>
                  <Button variant="secondary" size="sm" onClick={() => markAllAsRead?.()}>Segna tutte come lette</Button>
                </div>
                {(notifications || []).slice(0,5).map(n => (
                  <div key={n.id} style={{ display: 'flex', gap: 12, padding: '0.75rem', alignItems: 'flex-start', borderBottom: `1px solid ${themeStyles.border}` }}>
                    <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px -15px rgba(6, 182, 212, 0.55)' }}>{n.type === 'warning' ? '⚠' : n.type === 'success' ? '✓' : 'ℹ'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: n.read ? 500 : 700, color: themeStyles.text }}>{n.title}</div>
                      <div style={{ fontSize: 13, color: themeStyles.textSecondary, marginTop: 6 }}>{n.message}</div>
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        {!n.read && <Button variant="secondary" size="sm" onClick={() => markAsRead(n.id)}>Segna</Button>}
                        <Button variant="secondary" size="sm" onClick={() => deleteNotification(n.id)}>Elimina</Button>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(226, 232, 240, 0.6)' }}>{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                ))}
                {(notifications || []).length === 0 && <div style={{ padding: '1rem', textAlign: 'center', color: 'rgba(226, 232, 240, 0.6)' }}>Nessuna notifica</div>}
              </div>
            )}
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '22px',
            background: themeStyles.accentGradient,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.85rem',
            fontWeight: '700',
            cursor: 'pointer',
            border: `1px solid rgba(255, 255, 255, 0.22)`,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            boxShadow: themeStyles.glow
          }}
          onClick={() => navigate('/profile')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}>
            {getInitials(user, user)}
          </div>

          <div className="relative" ref={settingsRef}>
            <button
              onClick={toggleSettings}
              aria-label="Apri menu"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f1f5f9',
                border: '1px solid rgba(2,6,23,0.06)',
                color: '#0f172a',
                cursor: 'pointer'
              }}
              className="desktop-only-settings"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `${themeStyles.glow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(255, 255, 255, 0.05)';
              }}
            >
              <Icon name="hamburger" size={48} />
            </button>

            {showSettings && (
              <div
                className="settings-dropdown"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  width: '320px',
                  background: themeStyles.cardGlass,
                  backdropFilter: 'blur(24px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(160%)',
                  borderTop: `1px solid ${themeStyles.border}`,
                  borderRadius: '24px',
                  boxShadow: themeStyles.cardShadow,
                  zIndex: 1200,
                  overflow: 'hidden'
                }}
              >
                {/* Header del menu */}
                <div
                  style={{
                    padding: '1.5rem 1.5rem 2rem',
                    borderBottom: `1px solid ${themeStyles.border}`,
                    background: themeStyles.accentGradient,
                    borderTopLeftRadius: 24,
                    borderTopRightRadius: 24
                  }}>
                  {user && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2rem'
                    }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '25px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1rem',
                        fontWeight: '700',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        {getInitials(user, user)}
                      </div>
                      <div>
                        <div style={{
                          color: 'white',
                          fontWeight: '700',
                          fontSize: '1.1rem'
                        }}>
                          {getUserDisplayName(user, user)} {getPremiumBadge(user)}
                        </div>
                        <div style={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.85rem',
                          textTransform: 'capitalize'
                        }}>
                          {userRole}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation items */}
                <div style={{ padding: '1rem' }}>
                  {getNavItems().map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        navigate(item.path);
                        setShowSettings(false);
                        setLastClickedRoute(item.path);
                      }}
                      style={{
                        width: '90%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '1rem 1.25rem',
                        margin: '.6rem 1rem',
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: `1px solid ${themeStyles.border}`,
                        borderRadius: '18px',
                        color: themeStyles.text,
                        fontSize: '1.15rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.25s ease',
                        textAlign: 'left',
                        boxShadow: '0 10px 28px -18px rgba(6, 182, 212, 0.45)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <span style={{ fontSize: '1.6rem', width: 36, textAlign: 'center' }}><Icon name={item.icon} size={24} /></span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                    </button>
                  ))}

                  {/* Logout */}
                  <div style={{
                    marginTop: '1.5rem',
                    paddingTop: '1rem',
                    borderTop: `1px solid ${themeStyles.border}`
                  }}>
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowSettings(false);
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2rem',
                        padding: '1rem 4rem',
                        marginTop: '1rem',
                        background: 'linear-gradient(135deg, #f87171 0%, #ef4444 45%, #b91c1c 100%)',
                        border: 'none',
                        borderRadius: '24px',
                        color: 'white',
                        fontSize: '1.2rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 16px 32px -18px rgba(248, 113, 113, 0.65)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #f87171 0%, #ef4444 45%, #b91c1c 100%)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Icon name="logout" size={24} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {!token && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <nav style={{ display: 'flex', gap: 8 }}>
            <Link to="/search" style={{ textDecoration: 'none' }} onClick={() => setLastClickedRoute('/search') }>
              <Button variant="secondary" size="sm">Cerca proprietà</Button>
            </Link>
            <Link to="/tenant-search" style={{ textDecoration: 'none' }} onClick={() => setLastClickedRoute('/tenant-search') }>
              <Button variant="secondary" size="sm">Cerca inquilini</Button>
            </Link>
          </nav>

          <Link to="/login" style={{ textDecoration: 'none' }}>
            <Button variant="primary" size="md">Accedi / Registrati</Button>
          </Link>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          .desktop-only-settings {
            display: block;
          }
          @media (max-width: 768px) {
            .desktop-only-settings {
              display: none;
            }
          }
        `
      }} />
    </header>
  );
});

Header.displayName = 'Header';

export default Header;
